import { useMemo, useState } from "react";

import styled from "styled-components";
import { SWReactIcons } from "@similarweb/icons";

import type { ColumnsType } from "@similarweb/pro-logic-components";
import { Table, TextCell } from "@similarweb/pro-logic-components";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { BasicModal } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { TrafficShare } from "app/components/React/Table/cells";
import { translate } from "app/filters/i18nFilter";

import type { DomainShare } from "./types/GeoDataTypes";

const StyledInput = styled.input`
    ${setFontVariant("input2")};
    width: 100%;
    height: 100%;
    border: none;
    outline: none;
    background: transparent;
`;

type ColumnDataItem = {
    domain: string;
    trafficShare: number;
};

const columnsConfig: ColumnsType<ColumnDataItem> = [
    {
        dataIndex: "domain",
        get title() {
            return translate("common.domain");
        },
        key: "domain",
        render: (value) => <TextCell fontVariant="input2">{value}</TextCell>,
    },
    {
        dataIndex: "trafficShare",
        get title() {
            return translate("common.tables.columns.traffic.share.title");
        },
        key: "trafficShare",
        render: (value, row) => {
            return (
                <TrafficShare
                    value={value}
                    row={row}
                />
            );
        },
    },
];

type RegionalsCountryDomainsModalProps = {
    parentDomainName: string;
    domains: DomainShare[];
    isOpen: boolean;
    onClose: () => void;
};

export const RegionalsCountryDomainsModal = ({
    parentDomainName,
    domains,
    isOpen,
    onClose,
}: RegionalsCountryDomainsModalProps) => {
    const [searchQuery, setSearchQuery] = useState("");

    const tableData = useMemo(() => {
        if (!domains) {
            return [];
        }

        const searchRegex = new RegExp(searchQuery.toLowerCase(), "i");
        const filteredDomains = searchQuery ? domains.filter(({ domain }) => searchRegex.test(domain)) : domains;

        return filteredDomains.map(({ domain, totalShare }) => ({
            domain,
            trafficShare: totalShare || 0,
        }));
    }, [domains, searchQuery]);

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
    };

    return (
        <BasicModal
            isOpen={isOpen}
            customStyles={{ overlay: { zIndex: 1110 }, content: { width: "550px", height: "630px", padding: 0 } }}
            closeIconConfig={{
                show: true,
                color: colorsPalettes.carbon[300],
                onClick: () => onClose(),
            }}
        >
            <Flex
                flexDirection="column"
                height="100%"
            >
                <Flex
                    flexDirection="column"
                    gap="8px"
                    paddingX="24px"
                    paddingY="20px"
                >
                    <Text fontVariant="h5">{parentDomainName}</Text>
                    <Flex
                        gap="8px"
                        alignItems="center"
                    >
                        <SWReactIcons
                            iconName="regionals-world"
                            size="xs"
                            iconColor={colorsPalettes.carbon[200]}
                        />
                        <Text fontVariant="chip1">
                            {translate("market.players.market.shares.regionals.modal.subtitle", {
                                domainsCount: domains?.length,
                            })}
                        </Text>
                    </Flex>
                </Flex>
                <Flex
                    alignItems="center"
                    height="48px"
                    paddingX="24px"
                    paddingY="8px"
                    borderTop={`1px solid ${colorsPalettes.carbon[100]}`}
                    borderBottom={`1px solid ${colorsPalettes.carbon[100]}`}
                >
                    <Flex
                        gap="8px"
                        alignItems="center"
                        width="100%"
                    >
                        <SWReactIcons
                            iconName="search"
                            size="sm"
                            iconColor={colorsPalettes.carbon[200]}
                        />
                        <StyledInput
                            placeholder={translate("common.placeholder.search")}
                            onChange={handleSearch}
                        />
                    </Flex>
                </Flex>
                <Flex
                    flex="1"
                    overflow="scroll"
                >
                    <Table
                        sticky
                        data={tableData}
                        columnConfig={columnsConfig}
                        pagination={{ pageSize: domains?.length + 1 }}
                    />
                </Flex>
            </Flex>
        </BasicModal>
    );
};
